import {
  ConditionLogicConfigurationCollection,
  ConditionLogicId,
  IAndConditions,
  IConditionLogicConfiguration,
  IOrConditions,
} from '@principle-theorem/principle-core/interfaces';
import { first } from 'lodash';
import { v4 as uuid } from 'uuid';

export const CONDITION_LABEL_MAP: { [key in ConditionLogicId]: string } = {
  [ConditionLogicId.Always]: 'Always',
  [ConditionLogicId.Never]: 'Never',
  [ConditionLogicId.PatientHasTag]: 'Patient Has Tag',
  [ConditionLogicId.PatientHasMobileNumber]: 'Patient Has Mobile Number',
  [ConditionLogicId.PatientHasEmailAddress]: 'Patient Has Email Address',
  [ConditionLogicId.PatientHasIssuedForm]: 'Patient Has Issued Form',
  [ConditionLogicId.PatientHasSubmittedForm]: 'Patient Has Submitted Form',
  [ConditionLogicId.AppointmentHasTag]: 'Appointment Has Tag',
  [ConditionLogicId.AppointmentIsStatus]: 'Appointment Is Status',
  [ConditionLogicId.AppointmentConfirmed]: 'Appointment Confirmed',
  [ConditionLogicId.IsFirstAppointment]: 'Is First Appointment',
  [ConditionLogicId.AppointmentIncludesTreatment]:
    'Appointment Includes Treatment',
  [ConditionLogicId.AppointmentExcludesTreatment]:
    'Appointment Excludes Treatment',
  [ConditionLogicId.AppointmentIsTreatmentCategory]:
    'Appointment Is Treatment Category',
  [ConditionLogicId.AppointmentIsNotTreatmentCategory]:
    'Appointment Is Not Treatment Category',
  [ConditionLogicId.AppointmentIncludesPractitioner]:
    'Appointment Includes Practitioner',
  [ConditionLogicId.AppointmentExcludesPractitioner]:
    'Appointment Excludes Practitioner',
  [ConditionLogicId.InvoiceIsPaid]: 'Invoice Is Paid',
  [ConditionLogicId.InvoiceIsOverdue]: 'Invoice Is Overdue',
  [ConditionLogicId.AppointmentDepositIsUnpaid]:
    'Appointment Deposit Is Unpaid',
};

export class ConditionLogic {
  static collection(
    andConditions: IAndConditions[]
  ): ConditionLogicConfigurationCollection {
    return this.or(andConditions);
  }

  static or(andConditions: IAndConditions[]): IOrConditions {
    return {
      uid: uuid(),
      or: andConditions,
    };
  }

  static and(
    conditions: IConditionLogicConfiguration<unknown>[] = []
  ): IAndConditions {
    return {
      uid: uuid(),
      and: conditions,
    };
  }

  static never(): IConditionLogicConfiguration<unknown> {
    return {
      uid: uuid(),
      conditionId: ConditionLogicId.Never,
      config: {},
    };
  }

  static always(): IConditionLogicConfiguration<unknown> {
    return {
      uid: uuid(),
      conditionId: ConditionLogicId.Always,
      config: {},
    };
  }

  static custom<T>(
    id: ConditionLogicId,
    config: T
  ): IConditionLogicConfiguration<T> {
    return {
      uid: uuid(),
      conditionId: id,
      config,
    };
  }

  static getConditionCount(
    conditions?: ConditionLogicConfigurationCollection
  ): number {
    if (!conditions || ConditionLogic.willAlwaysPass(conditions)) {
      return 0;
    }
    return conditions.or.reduce(
      (count, andGroup) => count + andGroup.and.length,
      0
    );
  }

  /**
   * Checks if the provided ConditionLogicConfigurationCollection
   * contains an "Always" condition that will cause it to always pass.
   */
  static willAlwaysPass(
    conditions: ConditionLogicConfigurationCollection
  ): boolean {
    for (const andCondition of conditions.or) {
      if (andCondition.and.length > 1) {
        continue;
      }
      const condition = first(andCondition.and);
      if (condition && condition.conditionId === ConditionLogicId.Always) {
        return true;
      }
    }
    return false;
  }
}
